import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { capitalCase } from 'change-case';
import { Tags } from 'lucide-react';
import { BiDuplicate } from 'react-icons/bi';
import {
    TbArchive,
    TbArchiveOff,
    TbEdit,
    TbExternalLink,
    TbFolderSymlink,
    TbHistory,
    TbLink,
    TbLock,
    TbLockOpen,
    TbStar,
    TbStarOff,
    TbTrash,
} from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useResourceHistory } from '@/contexts/02-Authenticated';
import { useLifecycleResourceActions } from '@/hooks/useLifecycleResourceActions';
import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { useCreateLifecycle, useLifecycleDetail } from '@/queries/tanstack/database/entities';
import { lifecycleDuplicateInput } from '@/services';
import { MergeIcon, TagAssignmentDropdown } from '@/shared';
import {
    getWorkspaceResourceActionKeys,
    getWorkspaceResourceRemovalActionLabel,
    WorkspaceResourceActionKey,
    WorkspaceResourceActionPermission,
    WorkspaceResourceActionResourceType,
} from '@/shared/components/dropdowns/WorkspaceResourceDropdowns/helpers';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import MergeResourceModal from '@/shared/components/modals/ResourceActionModals/04-MergeResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useTeamStore,
} from '@/stores';
import { Command, isVersionedResourceType, ResourceHistoryTarget, WorkspaceSubjectType } from '@/types';

interface UseCommandPaletteResourceContextInput {
    open: boolean;
}

export interface CommandPaletteResourceContext {
    detailRoute?: (id: string) => string;
    duplicate: (id: string) => void;
    fetchById: (id: string) => void;
    getArchived: (item: any) => boolean;
    getFavorited: (item: any) => boolean;
    getLocked: (item: any) => boolean;
    getTitle: (item: any) => string;
    id: string;
    items: any[];
    match: RegExpMatchArray;
    parentLabel: string;
    parentRoute: string;
    resource: any;
    resourceType: WorkspaceResourceActionResourceType;
    route: string;
    selected: any;
    singularResource: string;
    title: string;
    toggleArchive: (id: string, value: boolean) => void;
    toggleFavorite: (id: string, value: boolean) => void;
    toggleLock: (id: string, value: boolean) => void;
}

const resourceActionIconStyle = { marginRight: '0.375rem' };

const actionPermissionByKey: Record<WorkspaceResourceActionKey, WorkspaceResourceActionPermission> = {
    archived: 'manage',
    copyLink: 'read',
    delete: 'manage',
    duplicate: 'read',
    favorited: 'update',
    history: 'read',
    locked: 'manage',
    merge: 'update',
    moveToFolder: 'update',
    openInNewWindow: 'read',
    rename: 'update',
    tags: 'update',
};

const subjectTypeByResourceType: Record<WorkspaceResourceActionResourceType, WorkspaceSubjectType> = {
    dashboards: WorkspaceSubjectType.DASHBOARD,
    folders: WorkspaceSubjectType.FOLDER,
    investments: WorkspaceSubjectType.INVESTMENT,
    lifecycles: WorkspaceSubjectType.LIFECYCLE,
    lists: WorkspaceSubjectType.LIST,
    memos: WorkspaceSubjectType.MEMO,
    pipelines: WorkspaceSubjectType.PIPELINE,
    teams: WorkspaceSubjectType.TEAM,
};

export const useCommandPaletteResourceContext = ({ open }: UseCommandPaletteResourceContextInput) => {
    const [resourceContextDismissed, setResourceContextDismissed] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [moveToFolderOpen, setMoveToFolderOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [tagsOpen, setTagsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const lifecycleMatch = useMemo(() => location.pathname.match(/^\/lifecycles\/([^/]+)/), [location.pathname]);
    const lifecycleId = lifecycleMatch?.[1];
    const lifecycleDetailQuery = useLifecycleDetail(lifecycleId, { enabled: open && Boolean(lifecycleId) });
    const lifecycle = lifecycleDetailQuery.data;
    const createLifecycle = useCreateLifecycle();
    const lifecycleResourceActions = useLifecycleResourceActions(lifecycle);
    const { openResourceHistory } = useResourceHistory();
    const {
        folders,
        selectedFolder,
        duplicateFolder,
        fetchFolderById,
        toggleFolderArchive,
        toggleFolderFavorite,
        toggleFolderLock,
    } = useFolderStore();
    const {
        investments,
        selectedInvestment,
        duplicateInvestment,
        fetchInvestmentById,
        toggleInvestmentArchive,
        toggleInvestmentFavorite,
        toggleInvestmentLock,
    } = useInvestmentStore();
    const { memos, selectedMemo, duplicateMemo, fetchMemoById, toggleMemoArchive, toggleMemoFavorite, toggleMemoLock } =
        useMemoStore();
    const {
        pipelines,
        selectedPipeline,
        duplicatePipeline,
        fetchPipelineById,
        togglePipelineArchive,
        togglePipelineFavorite,
        togglePipelineLock,
    } = usePipelineStore();
    const {
        dashboards,
        selectedDashboard,
        duplicateDashboard,
        fetchDashboardById,
        toggleDashboardArchive,
        toggleDashboardFavorite,
        toggleDashboardLock,
    } = useDashboardStore();
    const { lists, selectedList, duplicateList, fetchListById, toggleListArchive, toggleListFavorite, toggleListLock } =
        useListStore();
    const { teams, selectedTeam, duplicateTeam, fetchTeamById, toggleTeamArchive, toggleTeamFavorite, toggleTeamLock } =
        useTeamStore();
    const createDashboardAccess = useWorkspaceResourceCreateAccess('dashboards', dashboards?.length ?? 0);
    const createFolderAccess = useWorkspaceResourceCreateAccess('folders', folders?.length ?? 0);
    const createInvestmentAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);
    const createListAccess = useWorkspaceResourceCreateAccess('lists', lists?.length ?? 0);
    const createMemoAccess = useWorkspaceResourceCreateAccess('memos', memos?.length ?? 0);
    const createPipelineAccess = useWorkspaceResourceCreateAccess('pipelines', pipelines?.length ?? 0);
    const createTeamAccess = useWorkspaceResourceCreateAccess('teams', teams?.length ?? 0);

    useEffect(() => {
        if (open) {
            setResourceContextDismissed(false);
        }
    }, [location.pathname, open]);

    const resourceContext = useMemo<CommandPaletteResourceContext | null>(() => {
        const configs = [
            {
                match: location.pathname.match(/^\/folders\/([^/]+)/),
                resourceType: 'folders' as const,
                singularResource: 'folder',
                parentLabel: 'Folders',
                parentRoute: '/folders',
                items: folders,
                selected: selectedFolder,
                fetchById: fetchFolderById,
                getTitle: (item: any) => item?.name,
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: toggleFolderFavorite,
                toggleLock: toggleFolderLock,
                toggleArchive: toggleFolderArchive,
                duplicate: duplicateFolder,
            },
            {
                match: location.pathname.match(/^\/investments\/([^/]+)/),
                resourceType: 'investments' as const,
                singularResource: 'investment',
                parentLabel: 'Investments',
                parentRoute: '/investments',
                items: investments,
                selected: selectedInvestment,
                fetchById: fetchInvestmentById,
                getTitle: (item: any) => item?.editedName || item?.name,
                getFavorited: (item: any) => item?.isFavorite,
                getLocked: (item: any) => item?.isLocked,
                getArchived: (item: any) => item?.isArchived,
                toggleFavorite: toggleInvestmentFavorite,
                toggleLock: toggleInvestmentLock,
                toggleArchive: toggleInvestmentArchive,
                duplicate: duplicateInvestment,
            },
            {
                match: location.pathname.match(/^\/memos\/([^/]+)/),
                resourceType: 'memos' as const,
                singularResource: 'memo',
                parentLabel: 'Memos',
                parentRoute: '/memos',
                items: memos,
                selected: selectedMemo,
                fetchById: fetchMemoById,
                getTitle: (item: any) => item?.title || 'Untitled Memo',
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: toggleMemoFavorite,
                toggleLock: toggleMemoLock,
                toggleArchive: toggleMemoArchive,
                duplicate: duplicateMemo,
            },
            {
                match: location.pathname.match(/^\/pipelines\/([^/]+)/),
                resourceType: 'pipelines' as const,
                singularResource: 'pipeline',
                parentLabel: 'Pipelines',
                parentRoute: '/pipelines',
                items: pipelines,
                selected: selectedPipeline,
                fetchById: fetchPipelineById,
                getTitle: (item: any) => item?.name,
                getFavorited: (item: any) => item?.isFavorite,
                getLocked: (item: any) => item?.isLocked,
                getArchived: (item: any) => item?.isArchived,
                toggleFavorite: togglePipelineFavorite,
                toggleLock: togglePipelineLock,
                toggleArchive: togglePipelineArchive,
                duplicate: duplicatePipeline,
            },
            {
                match: location.pathname.match(/^\/dashboards\/([^/]+)/),
                resourceType: 'dashboards' as const,
                singularResource: 'dashboard',
                parentLabel: 'Dashboards',
                parentRoute: '/relations/dashboards',
                items: dashboards,
                selected: selectedDashboard,
                fetchById: fetchDashboardById,
                getTitle: (item: any) => item?.name,
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: toggleDashboardFavorite,
                toggleLock: toggleDashboardLock,
                toggleArchive: toggleDashboardArchive,
                duplicate: duplicateDashboard,
            },
            {
                match: location.pathname.match(/^\/lists\/([^/]+)/),
                resourceType: 'lists' as const,
                singularResource: 'list',
                parentLabel: 'Lists',
                parentRoute: '/relations/lists',
                items: lists,
                selected: selectedList,
                fetchById: fetchListById,
                getTitle: (item: any) => item?.name,
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: toggleListFavorite,
                toggleLock: toggleListLock,
                toggleArchive: toggleListArchive,
                duplicate: duplicateList,
            },
            {
                match: lifecycleMatch,
                resourceType: 'lifecycles' as const,
                singularResource: 'lifecycle',
                parentLabel: 'Lifecycles',
                parentRoute: '/lifecycles',
                items: lifecycle ? [lifecycle] : [],
                selected: lifecycle,
                fetchById: () => undefined,
                getTitle: (item: any) => item?.label,
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: () => void lifecycleResourceActions.toggleFavorite(),
                toggleLock: () => void lifecycleResourceActions.toggleLock(),
                toggleArchive: () => void lifecycleResourceActions.toggleArchive(),
                duplicate: () => {
                    if (!lifecycle) {
                        return;
                    }

                    void createLifecycle
                        .mutateAsync(lifecycleDuplicateInput(lifecycle))
                        .then((copy) => navigate(`/lifecycles/${copy.id}`));
                },
            },
            {
                match: location.pathname.match(/^\/settings\/workspace\/permissions\/teams\/([^/]+)/),
                resourceType: 'teams' as const,
                singularResource: 'team',
                parentLabel: 'Teams',
                parentRoute: '/settings/workspace/permissions/teams',
                detailRoute: (id: string) => `/settings/workspace/permissions/teams/${id}`,
                items: teams,
                selected: selectedTeam,
                fetchById: fetchTeamById,
                getTitle: (item: any) => item?.name,
                getFavorited: (item: any) => item?.favorited,
                getLocked: (item: any) => item?.locked,
                getArchived: (item: any) => item?.archived,
                toggleFavorite: toggleTeamFavorite,
                toggleLock: toggleTeamLock,
                toggleArchive: toggleTeamArchive,
                duplicate: duplicateTeam,
            },
        ].find((config) => config.match);

        if (!configs?.match) {
            return null;
        }

        const match = configs.match;
        const id = match[1];
        const selectedMatches = configs.selected?.id === id;
        const resource = selectedMatches ? configs.selected : configs.items?.find((item: any) => item?.id === id);

        return {
            ...configs,
            match,
            id,
            resource,
            title: configs.getTitle(resource) || capitalCase(configs.singularResource),
            route: configs.detailRoute?.(id) ?? `/${configs.resourceType}/${id}`,
        };
    }, [
        createLifecycle,
        dashboards,
        duplicateDashboard,
        duplicateFolder,
        duplicateInvestment,
        duplicateList,
        duplicateMemo,
        duplicatePipeline,
        duplicateTeam,
        fetchDashboardById,
        fetchFolderById,
        fetchInvestmentById,
        fetchListById,
        fetchMemoById,
        fetchPipelineById,
        fetchTeamById,
        folders,
        investments,
        lifecycle,
        lifecycleMatch,
        lifecycleResourceActions,
        lists,
        location.pathname,
        memos,
        navigate,
        pipelines,
        selectedDashboard,
        selectedFolder,
        selectedInvestment,
        selectedList,
        selectedMemo,
        selectedPipeline,
        selectedTeam,
        teams,
        toggleDashboardArchive,
        toggleDashboardFavorite,
        toggleDashboardLock,
        toggleFolderArchive,
        toggleFolderFavorite,
        toggleFolderLock,
        toggleInvestmentArchive,
        toggleInvestmentFavorite,
        toggleInvestmentLock,
        toggleListArchive,
        toggleListFavorite,
        toggleListLock,
        toggleMemoArchive,
        toggleMemoFavorite,
        toggleMemoLock,
        togglePipelineArchive,
        togglePipelineFavorite,
        togglePipelineLock,
        toggleTeamArchive,
        toggleTeamFavorite,
        toggleTeamLock,
    ]);

    const readAccess = useResourceActionAccess(resourceContext?.resourceType, 'read', resourceContext?.id);
    const updateAccess = useResourceActionAccess(resourceContext?.resourceType, 'update', resourceContext?.id);
    const manageAccess = useResourceActionAccess(resourceContext?.resourceType, 'manage', resourceContext?.id);
    const historyTarget = useMemo<ResourceHistoryTarget | undefined>(
        () =>
            resourceContext && isVersionedResourceType(resourceContext.resourceType)
                ? {
                      resourceId: resourceContext.id,
                      resourceType: resourceContext.resourceType,
                      title: resourceContext.title,
                  }
                : undefined,
        [resourceContext],
    );

    const resourceContextCommands = useMemo<Command[]>(() => {
        if (!resourceContext?.resource || resourceContextDismissed) {
            return [];
        }

        const accessByPermission = {
            manage: manageAccess,
            read: readAccess,
            update: updateAccess,
        };
        const createAccessByResourceType: Partial<
            Record<WorkspaceResourceActionResourceType, ReturnType<typeof useWorkspaceResourceCreateAccess>>
        > = {
            dashboards: createDashboardAccess,
            folders: createFolderAccess,
            investments: createInvestmentAccess,
            lists: createListAccess,
            memos: createMemoAccess,
            pipelines: createPipelineAccess,
            teams: createTeamAccess,
        };

        const title = resourceContext.title;
        const copyLink = () => {
            const origin = import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io';

            navigator.clipboard.writeText(`${origin}${resourceContext.route}`).then(
                () => {
                    toast('Link Copied', {
                        icon: <TbLink size='1rem' />,
                        description: title,
                    });
                },
                (error) => {
                    captureException(error);
                    toast('Error Copying Link', { description: title });
                },
            );
        };

        const command = (
            key: WorkspaceResourceActionKey,
            label: string,
            icon: Command['icon'],
            onSelect: () => void,
            externalLink = false,
        ): Command => {
            const access = accessByPermission[actionPermissionByKey[key]];
            const createAccess =
                key === 'duplicate' ? createAccessByResourceType[resourceContext.resourceType] : undefined;
            const disabled = access.disabled || Boolean(createAccess?.disabled);

            return {
                disabled,
                disabledReason: access.disabled ? 'Insufficient permissions' : createAccess?.disabledReason,
                externalLink,
                group: 'resourceActions',
                icon,
                id: `${resourceContext.resourceType}:${key}:${resourceContext.id}`,
                label,
                onSelect,
            };
        };
        const commandsByKey: Record<WorkspaceResourceActionKey, Command> = {
            archived: command(
                'archived',
                resourceContext.getArchived(resourceContext.resource) ? 'Unarchive' : 'Archive',
                resourceContext.getArchived(resourceContext.resource) ? (
                    <TbArchiveOff size='1rem' style={resourceActionIconStyle} />
                ) : (
                    <TbArchive size='1rem' style={resourceActionIconStyle} />
                ),
                () =>
                    resourceContext.toggleArchive(
                        resourceContext.id,
                        !resourceContext.getArchived(resourceContext.resource),
                    ),
            ),
            copyLink: command(
                'copyLink',
                'Copy Link',
                <TbLink size='1rem' style={resourceActionIconStyle} />,
                copyLink,
            ),
            delete: command(
                'delete',
                getWorkspaceResourceRemovalActionLabel(resourceContext.resourceType),
                <TbTrash size='1rem' style={resourceActionIconStyle} />,
                () => setDeleteOpen(true),
            ),
            duplicate: command(
                'duplicate',
                'Duplicate',
                <BiDuplicate size='1rem' style={resourceActionIconStyle} />,
                () => {
                    const createAccess = createAccessByResourceType[resourceContext.resourceType];

                    if (!createAccess?.disabled) {
                        resourceContext.duplicate(resourceContext.id);
                    }
                },
            ),
            favorited: command(
                'favorited',
                resourceContext.getFavorited(resourceContext.resource) ? 'Remove from Favorites' : 'Add to Favorites',
                resourceContext.getFavorited(resourceContext.resource) ? (
                    <TbStarOff size='1rem' style={resourceActionIconStyle} />
                ) : (
                    <TbStar size='1rem' style={resourceActionIconStyle} />
                ),
                () =>
                    resourceContext.toggleFavorite(
                        resourceContext.id,
                        !resourceContext.getFavorited(resourceContext.resource),
                    ),
            ),
            history: command(
                'history',
                'Show History',
                <TbHistory size='1rem' style={resourceActionIconStyle} />,
                () => historyTarget && openResourceHistory(historyTarget),
            ),
            locked: command(
                'locked',
                resourceContext.getLocked(resourceContext.resource) ? 'Unlock' : 'Lock',
                resourceContext.getLocked(resourceContext.resource) ? (
                    <TbLockOpen size='1rem' style={resourceActionIconStyle} />
                ) : (
                    <TbLock size='1rem' style={resourceActionIconStyle} />
                ),
                () =>
                    resourceContext.toggleLock(
                        resourceContext.id,
                        !resourceContext.getLocked(resourceContext.resource),
                    ),
            ),
            merge: command(
                'merge',
                'Merge',
                <span style={resourceActionIconStyle}>
                    <MergeIcon />
                </span>,
                () => setMergeOpen(true),
            ),
            moveToFolder: command(
                'moveToFolder',
                'Move to Folder',
                <TbFolderSymlink size='1rem' style={resourceActionIconStyle} />,
                () => setMoveToFolderOpen(true),
            ),
            openInNewWindow: command(
                'openInNewWindow',
                'Open in New Window',
                <TbExternalLink size='1rem' style={resourceActionIconStyle} />,
                () => window.open(resourceContext.route, '_blank'),
                true,
            ),
            rename: command('rename', 'Rename', <TbEdit size='1rem' style={resourceActionIconStyle} />, () =>
                setRenameOpen(true),
            ),
            tags: command('tags', 'Manage Tags', <Tags size='1rem' style={resourceActionIconStyle} />, () =>
                setTagsOpen(true),
            ),
        };

        return getWorkspaceResourceActionKeys(resourceContext.resourceType).map((key) => commandsByKey[key]);
    }, [
        createDashboardAccess,
        createFolderAccess,
        createInvestmentAccess,
        createListAccess,
        createMemoAccess,
        createPipelineAccess,
        createTeamAccess,
        historyTarget,
        manageAccess,
        openResourceHistory,
        readAccess,
        resourceContext,
        resourceContextDismissed,
        updateAccess,
    ]);

    const resourceContextModals =
        resourceContext?.resource && historyTarget ? (
            <>
                <RenameResourceModal
                    open={renameOpen}
                    resource={resourceContext.resource}
                    setOpen={setRenameOpen}
                    singularResource={resourceContext.singularResource}
                />
                {!['folders', 'teams'].includes(resourceContext.resourceType) ? (
                    <MoveResourceToFolderModal
                        open={moveToFolderOpen}
                        resource={resourceContext.resource}
                        setOpen={setMoveToFolderOpen}
                        singularResource={resourceContext.singularResource}
                    />
                ) : undefined}
                <DeleteResourceModal
                    open={deleteOpen}
                    resource={resourceContext.resource}
                    setOpen={setDeleteOpen}
                    singularResource={resourceContext.singularResource}
                />
                <MergeResourceModal open={mergeOpen} setOpen={setMergeOpen} source={historyTarget} />
                <TagAssignmentDropdown
                    anchorStyle={{
                        height: 1,
                        left: '50vw',
                        pointerEvents: 'none',
                        position: 'fixed',
                        top: '50vh',
                        width: 1,
                    }}
                    onOpenChange={setTagsOpen}
                    open={tagsOpen}
                    placement='bottom'
                    resourceLabel={resourceContext.title}
                    subject={{
                        subjectId: resourceContext.id,
                        subjectLabel: resourceContext.title,
                        subjectType: subjectTypeByResourceType[resourceContext.resourceType],
                    }}
                    trigger={[]}
                >
                    <span aria-hidden />
                </TagAssignmentDropdown>
            </>
        ) : undefined;

    return {
        isResourceContextMode: Boolean(resourceContext?.resource && !resourceContextDismissed),
        resourceContext,
        resourceContextCommands,
        resourceContextDismissed,
        resourceContextModals,
        setResourceContextDismissed: setResourceContextDismissed as Dispatch<SetStateAction<boolean>>,
    };
};

export type CommandPaletteResourceContextState = ReturnType<typeof useCommandPaletteResourceContext>;
